// sonner, the one the app's toast() calls use — mounted here, once, for every
// route. It used to live in Layout, which the workspace sits outside, so every
// toast raised in /build (saves, downloads, errors) was dropped unseen. The
// shadcn Toaster that was mounted here instead was never called, and its empty
// full-width container sat across the top 32px of a phone swallowing taps on
// the workspace's back button, credits, Publish and menu.
import { Toaster } from "@/components/ui/sonner"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import NavigationTracker from '@/lib/NavigationTracker'
import { pagesConfig } from './pages.config'
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import SignedOutOnly from './lib/SignedOutOnly';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import WebsitePreview from './pages/WebsitePreview';
import Workspace from './pages/Workspace';
import ResetPassword from './pages/ResetPassword';
import ConfirmEmail from './pages/ConfirmEmail';
import MarketplaceListing from './pages/MarketplaceListing';
import PagePreloader from '@/components/PagePreloader';
import { useState, useEffect, Suspense, lazy } from 'react';
import { MotionConfig } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';

// BuildersLab Business. Lazy: most visits never open a company.
const Company = lazy(() => import('./pages/Company'));
const SharedCompany = lazy(() => import('./pages/SharedCompany'));
const PhoneUpload = lazy(() => import('./pages/PhoneUpload'));

const { Pages, Layout, mainPage } = pagesConfig;
const mainPageKey = mainPage ?? Object.keys(Pages)[0];
const MainPage = mainPageKey ? Pages[mainPageKey] : <></>;

const LayoutWrapper = ({ children, currentPageName }) => Layout ?
  <Layout currentPageName={currentPageName}>{children}</Layout>
  : <>{children}</>;

function LoadingSpinner() {
  return (
    <div className="fixed inset-0 flex items-center justify-center">
      <div
        role="status"
        aria-label="Loading"
        className="w-8 h-8 border-4 border-zinc-800 border-t-orange-500 rounded-full animate-spin"
      >
        <span className="sr-only">Loading…</span>
      </div>
    </div>
  );
}

const AuthenticatedApp = () => {
  const { loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Suspense fallback={<LoadingSpinner />}>
    <Routes>
      {/* The landing page is the pitch, and it is for people who have not
          signed up yet. A resolved session goes to the workspace dashboard
          instead — see SignedOutOnly for why. `/${mainPageKey}` below is the
          same page under a second URL (the navbar logo points there), so it
          carries the same guard. */}
      <Route path="/" element={
        <SignedOutOnly>
          <LayoutWrapper currentPageName={mainPageKey}>
            <MainPage />
          </LayoutWrapper>
        </SignedOutOnly>
      } />
      {/* The build workspace owns the whole screen, so it sits outside
          LayoutWrapper — no navbar, no footer, no marketing page mounted
          behind it. Still inside AuthenticatedApp, which holds the session
          until auth has resolved.

          One route with an optional param. A fresh build adopts its id
          mid-generation (/build → /build/:websiteId), and that transition must
          not remount the workspace — a remount re-runs GenerationView's init
          effect, restarting the generation and charging a second credit.
          Matching both shapes against a single route keeps that obvious.
          (WorkspaceRouteIdentity.test.jsx pins the no-remount behaviour; React
          Router also happens to preserve the instance across two separate
          routes, so this is for clarity rather than the only way to get it.) */}
      <Route path="/build/:websiteId?" element={<Workspace />} />
      {/* A company's whole life on one full-screen page, like the workspace
          (src/pages/Company.jsx). My companies lives in the dashboard. */}
      <Route path="/company/:companyId" element={<Company />} />
      <Route path="/companies" element={<Navigate to="/build?view=dashboard&section=companies" replace />} />
      {/* Declared explicitly rather than added to `Pages`, for two reasons: the
          emailed link is kebab-case while every generated route is PascalCase,
          and NavigationTracker reads `Pages` to name analytics events — a reset
          page does not belong in that list. */}
      <Route path="/reset-password" element={
        <LayoutWrapper currentPageName="ResetPassword">
          <ResetPassword />
        </LayoutWrapper>
      } />
      {/* The link mailed to a new address from Settings. Explicit for the same
          reasons as /reset-password. */}
      <Route path="/confirm-email" element={
        <LayoutWrapper currentPageName="ConfirmEmail">
          <ConfirmEmail />
        </LayoutWrapper>
      } />
      {/* One marketplace listing. The browse page is /Marketplace, generated
          from PAGES like every other page; a listing needs an id in its path,
          which the generated routes have no way to carry. */}
      <Route path="/marketplace/:listingId" element={
        <LayoutWrapper currentPageName="Marketplace">
          <MarketplaceListing />
        </LayoutWrapper>
      } />
      {Object.entries(Pages).map(([path, Page]) => {
        const page = (
          <LayoutWrapper currentPageName={path}>
            <Page />
          </LayoutWrapper>
        );
        return (
          <Route
            key={path}
            path={`/${path}`}
            element={path === mainPageKey ? <SignedOutOnly>{page}</SignedOutOnly> : page}
          />
        );
      })}
      <Route path="*" element={<PageNotFound />} />
    </Routes>
    </Suspense>
  );
};


function App() {
  // The toasts follow the site's theme. The shadcn wrapper asks next-themes,
  // whose provider this app never mounts, so it always answered "system".
  const { theme } = useTheme();
  const [showPreloader, setShowPreloader] = useState(() => {
    try {
      if (sessionStorage.getItem('bl_preloader_shown')) return false;
    } catch { /* private browsing may block sessionStorage */ }
    return true;
  });

  useEffect(() => {
    if (!showPreloader) return;
    const timer = setTimeout(() => {
      setShowPreloader(false);
      try { sessionStorage.setItem('bl_preloader_shown', '1'); } catch { /* ignore */ }
    }, 3200);
    return () => clearTimeout(timer);
  }, [showPreloader]);

  return (
    <MotionConfig reducedMotion="user">
      <AuthProvider>
        <QueryClientProvider client={queryClientInstance}>
          <Router>
            <NavigationTracker />
            <Routes>
              <Route path="/preview/:websiteId" element={<WebsitePreview />} />
              {/* No account needed: a founder's shared preview, and the phone
                  hand-off for an ID and signature. Each works through a
                  signed token, never a bare id. */}
              <Route path="/c/:token" element={<Suspense fallback={null}><SharedCompany /></Suspense>} />
              <Route path="/upload/:token" element={<Suspense fallback={null}><PhoneUpload /></Suspense>} />
              <Route path="*" element={<AuthenticatedApp />} />
            </Routes>
          </Router>
          <Toaster position="top-center" richColors theme={theme} />
          <PagePreloader isVisible={showPreloader} />
        </QueryClientProvider>
      </AuthProvider>
    </MotionConfig>
  )
}

export default App
